import React, { useEffect, useState } from "react";
import { Paper, alpha, Tabs, Tab, Box, Divider, Button } from "@mui/material";
import { getCommaOut } from "../../../../utils/hooks/dataHandler";
import PdfGenerator from "../Pdf/CaSchedulePdf";
import ScheduleHeader from "../../../App/ScheduleHeader";
import ScheduleNotes from "../../ScheduleNotes";
import {
  generateAdditions,
  generateDisposal,
  calculateTotal,
  updateRowValues,
  inputHandlerSwitch,
  calculateEnhancedSet,
  generateHirePurchaseRows,
  handleCaNameField,
  populateTwdvToDisposal,
} from "./utils/calculation";
import CaTable from "./CaTable";
import { getCaSettingByKey } from "../../../../builders/settings/CaSetting";
import ComponentLoading from "../../../App/ComponentLoading";
import CaSummary from "./CaSummary";
import SectionBox from "./utils/SectionBox";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BalanceIcon from "@mui/icons-material/Balance";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import PaymentsIcon from "@mui/icons-material/Payments";
import CaExcelUpload from "./CaExcelUpload";
import useTabPersistence from "../../../../utils/hooks/useTabPersistence";
import ColumnInfo from "./ColumnInfo";
import { useSuccessDialog } from "../../../../contexts/SuccessDialogContext";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";
import { getCaSettings } from "../../../../builders/settings/CaSetting";
import { caScheduleTypes } from "../../../../utils/static/CASchedule";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PopulatePriorYaDialog from "./PopulatePriorYaDialog";
import { getNetDisposalError } from "./utils/s19dNetDisposal";
import {
  getPriorYaDisposalOptions,
  getPriorYaDisposalKey,
} from "./utils/priorYaPopulation";

const CaSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
  getAvailableCaScheduleList,
  getCAScheduleDataByType,
  updateCASchedule,
  caScheduleTypes: caScheduleTypesProp,
}) => {
  const yearOfAssessment = company.yearOfAssessment;
  const [scheduleNo, setScheduleNo] = useState(null);
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const handleSuccess = useSuccessDialog();
  const handleError = useErrorDialog();

  const [loading, setLoading] = useState(true);

  const [caType, setCaType] = useState(null);
  const [caSummaryDetails, setCaSummaryDetails] = useState([]);

  // additionsDetails = Current YA CA claims
  // disposalDetails = Prior YA CA claims
  const [additionsDetails, setAdditionDetails] = useState([]);
  const [hirePurchaseAdditions, setHirePurchaseAdditions] = useState([]);
  const [hirePurchaseDisposal, setHirePurchaseDisposal] = useState([]);
  const [twdvDetails, setTwdvDetails] = useState([]);
  const [disposalDetails, setDisposalDetails] = useState([]);

  const [totalAdditionsDetails, setTotalAdditionsDetails] = useState({});
  const [totalHpAdditionsDetails, setTotalHpAdditionsDetails] = useState({});
  const [totalDisposalDetails, setTotalDisposalDetails] = useState({});
  const [totalHpDisposalDetails, setTotalHpDisposalDetails] = useState({});
  const [totalNetAA, setTotalNetAA] = useState(0);

  const [caSettings, setCaSettings] = useState([]);
  const [refSources, setRefSources] = useState({});

  const [fullscreenSection, setFullscreenSection] = useState(null); // 'disposal' | 'additions' | null
  const [populatePriorYaOpen, setPopulatePriorYaOpen] = useState(false);
  const priorYaDisposalOptions = getPriorYaDisposalOptions(
    twdvDetails,
    caSettings,
    caType,
    disposalDetails
  ).map((option) => ({
    ...option,
    alreadyAdded: disposalDetails.some(
      (row) =>
        getPriorYaDisposalKey(
          row.caName === "S16 - (LIA / IBA)" ? row.s16Type : row.caName,
          row.ya,
          row.noOfYearClaimsBf
        ) === option.groupKey
    ),
  }));

  // Excel upload dialog state
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const handleOpenExcelUploadDialog = () => setExcelDialogOpen(true);
  const handleCloseExcelUploadDialog = () => {
    setExcelDialogOpen(false);
    setProcessing(false);
  };

  const handleExcelUpload = async (excelData) => {
    setProcessing(true);
    try {
      const caList = await getAvailableCaScheduleList();

      if (!caList || caList.length === 0) {
        handleError("Unable to find any CA schedules. Please try again later.");
        handleCloseExcelUploadDialog();
        return;
      }

      const fixedCaKeys = ["S19A", "S19", "S19B", "S19D", "S16"];
      const listKeyTransform = {
        S19A: "S19ASchedule",
        S19: "S19Schedule",
        S19B: "S19BSchedule",
        S19D: "S19DSchedule",
        S16: "S16Schedule",
      };

      const scheduleTypes = caScheduleTypesProp || caScheduleTypes;
      let processedSchedules = [];
      let skippedKeys = [];
      let caNameErrors = [];

      // Process each CA schedule type
      for (const caKey of fixedCaKeys) {
        if (!excelData[caKey] || excelData[caKey].length === 0) {
          skippedKeys.push(caKey);
          continue;
        }

        const transformedCaKey = listKeyTransform[caKey];
        const caSchedule = caList.find(
          (ca) => ca.scheduleType === transformedCaKey
        );

        if (!caSchedule) {
          skippedKeys.push(caKey);
          continue;
        }

        try {
          // Get existing schedule data
          const response = await getCAScheduleDataByType(
            caSchedule.sch_id,
            scheduleTypes[transformedCaKey]
          );
          const scheduleData = response?.schedule;

          if (!scheduleData) {
            skippedKeys.push(caKey);
            continue;
          }

          // Get CA type (remove "Schedule" suffix)
          const caType = transformedCaKey.replace(/Schedule/g, "");
          let caSettings = scheduleData.caSetting || getCaSettings(caType);

          // Transform Excel data to disposal format
          const excelRows = excelData[caKey];
          let disposalRows = excelRows.reduce((acc, row) => {
            if (!row) return acc;
            let caDetails = getCaSettingByKey(caSettings, row.caName);
            if (!caDetails || caDetails === null) {
              caNameErrors.push(
                [row.ya, row.caName, row.noOfYearClaimsBf]
                  .filter(Boolean)
                  .join(" | ")
              );
              return acc;
            }
            if (row.caName !== "S16 - (LIA / IBA)") {
              row = handleCaNameField(
                row,
                row.caName,
                twdvDetails,
                caSettings,
                yearOfAssessment,
                false,
                true
              );
            } else {
              const s16Type = row.caSummary || "";
              row = handleCaNameField(
                row,
                s16Type,
                twdvDetails,
                caSettings,
                yearOfAssessment,
                false,
                true
              );
              row.caName = "S16 - (LIA / IBA)";
              row.caSummary = s16Type;
              row.s16Type = s16Type;
            }

            const disposalRow = {
              ya: row.ya || row["Year of Assessment"] || "",
              caName: row.caName || "",
              caSummary: row.caSummary || "",
              allowanceType: "Base",
              initialAllowanceRate: 0,
              s16Type: row.caSummary || "",
              noOfYearClaims: Number(row.noOfYearClaims) || 0,
              noOfYearClaimsBf: Number(row.noOfYearClaimsBf) || 0,
              twdvBf: Number(row.twdvBf) || Number(row.baseTwdvBf) || 0,
              currentAmount: 0,
              currentYearDisposal: 0,
              totalAmount: 0,
              isDeferred: false,
              isDisregard:
                row.isDisregard === true ||
                row.isDisregard === "TRUE" ||
                row.isDisregard === "True",
              disregardRate: Number(row.disregardRate) || 0,
              annualAllowance: 0,
              initialAllowance: 0,
              twdvCf: 0,
              noOfYearClaimsCf: 0,
              enhanced: {},
              disregard: {},
            };

            // Handle S19B enhanced TWDV
            if (caKey === "S19B" && row.enhancedTwdvBf) {
              disposalRow.enhanced = {
                twdvBf: Number(row.enhancedTwdvBf) || 0,
              };
            }

            acc.push(disposalRow);
            return acc;
          }, []);

          disposalRows = disposalRows.filter((row) => row !== null);

          // Merge with existing disposal data (replace existing entries with same key)
          const existingDisposals = scheduleData.dataRows?.disposal || [];
          const disposalMap = new Map();

          // Add existing disposals
          existingDisposals.forEach((d) => {
            const key = `${
              d.caName === "S16 - (LIA / IBA)" ? d.s16Type : d.caName
            }_${d.ya}_${d.noOfYearClaims}_${d.noOfYearClaimsBf}`;
            disposalMap.set(key, d);
          });

          // Add/update with Excel data
          disposalRows.forEach((row) => {
            const key = `${
              row.caName === "S16 - (LIA / IBA)" ? row.s16Type : row.caName
            }_${row.ya}_${row.noOfYearClaims}_${row.noOfYearClaimsBf}`;
            disposalMap.set(key, row);
          });

          const mergedDisposals = Array.from(disposalMap.values());

          // Process the data similar to useEffect
          const twdvSummary = scheduleData.TWDVSummary || [];
          const faAdditionsSummary = scheduleData.faAdditionsSummary || {};
          const hirePurchaseSummary = scheduleData.hirePurchaseSummary || {};

          // Generate disposal rows
          const processedDisposals = generateDisposal(
            mergedDisposals,
            twdvSummary,
            caSettings,
            caType,
            yearOfAssessment
          );

          // Generate additions (keep existing)
          const existingAdditions = scheduleData.dataRows?.additions || [];
          const processedAdditions = generateAdditions(
            existingAdditions,
            faAdditionsSummary,
            twdvSummary,
            caSettings,
            caType,
            yearOfAssessment,
            scheduleData.faRegisterImport?.dataRows || []
          );

          // Generate hire purchase rows
          const hpAdditions = scheduleData.dataRows?.hpAdditions || [];
          const hpDisposals = scheduleData.dataRows?.hpDisposal || [];
          const {
            additions: processedHpAdditions,
            disposals: processedHpDisposals,
          } = generateHirePurchaseRows(
            hpAdditions,
            hpDisposals,
            hirePurchaseSummary,
            caSettings,
            caType,
            yearOfAssessment
          );

          // Calculate totals
          const totalAdditionsDetails = calculateTotal(processedAdditions);
          const totalDisposalDetails = calculateTotal(processedDisposals);
          const totalHpAdditionsDetails = calculateTotal(
            processedHpAdditions,
            true
          );
          const totalHpDisposalDetails = calculateTotal(
            processedHpDisposals,
            true
          );

          // Calculate total net AA
          const totalNetAA =
            (Math.round(totalAdditionsDetails.initialAllowance) || 0) +
            Math.round(totalAdditionsDetails.annualAllowance) +
            (Math.round(totalDisposalDetails.initialAllowance) || 0) +
            Math.round(totalDisposalDetails.annualAllowance) +
            (Math.round(totalHpAdditionsDetails.initialAllowance) || 0) +
            Math.round(totalHpAdditionsDetails.annualAllowance) +
            (Math.round(totalHpDisposalDetails.initialAllowance) || 0) +
            Math.round(totalHpDisposalDetails.annualAllowance);

          // Generate CA summary
          let groupedAA = {};
          let groupedIA = {};

          const processRowsForSummary = (rows) => {
            if (!rows) return;
            rows.forEach((row) => {
              let caKey = `${row.caSummary}`;

              if (["S16 - LIA,S16 - IBA"].includes(caKey)) {
                if (row.s16Type) caKey = `${row.s16Type}`;
                else caKey = `${row.caName}`;
              }
              if (caKey === "S16" && row.isHirePurchase) {
                caKey = `${row.caName}`;
              }
              if (caKey && caKey !== "") {
                if (!groupedAA[caKey]) groupedAA[caKey] = 0;
                if (!groupedIA[caKey]) groupedIA[caKey] = 0;

                groupedAA[caKey] += Math.round(row.annualAllowance) || 0;
                groupedIA[caKey] += Math.round(row.initialAllowance) || 0;

                if (
                  row.allowanceType === "Enhanced" &&
                  Object.keys(row.enhanced).length > 0
                ) {
                  groupedAA[caKey] +=
                    Math.round(row.enhanced.annualAllowance) || 0;
                  groupedIA[caKey] +=
                    Math.round(row.enhanced.initialAllowance) || 0;
                }

                if (row.isDisregard && Object.keys(row.disregard).length > 0) {
                  groupedAA[caKey] -=
                    Math.round(row.disregard.annualAllowance) || 0;
                  groupedIA[caKey] -=
                    Math.round(row.disregard.initialAllowance) || 0;
                }
              }
            });
          };

          processRowsForSummary(processedAdditions);
          processRowsForSummary(processedDisposals);
          processRowsForSummary(processedHpAdditions);
          processRowsForSummary(processedHpDisposals);

          const caSummaryDetails = Object.keys(groupedAA).map((caSummary) => ({
            caSummary,
            totalAA: groupedAA[caSummary],
            totalIA: groupedIA[caSummary] || 0,
          }));

          // Prepare schedule data for saving
          const updatedSchedule = {
            sch_id: caSchedule.sch_id,
            scheduleType: scheduleTypes[transformedCaKey] || transformedCaKey,
            dataRows: {
              additions: processedAdditions,
              disposal: processedDisposals.sort((a, b) => a.ya - b.ya),
              hpAdditions: processedHpAdditions,
              hpDisposal: processedHpDisposals,
            },
            totalNetAA: totalNetAA,
            doc: JSON.stringify({
              caType,
              refSources: scheduleData.scheduleMapping || {},
              additionsDetails: processedAdditions,
              disposalDetails: processedDisposals,
              hirePurchaseAdditions: processedHpAdditions,
              hirePurchaseDisposal: processedHpDisposals,
              totalAdditionsDetails,
              totalDisposalDetails,
              totalHpAdditionsDetails,
              totalHpDisposalDetails,
              totalNetAA,
              caSummaryDetails,
              notes: scheduleData.notes || "",
              reviewNotes: scheduleData.reviewNotes || "",
            }),
            notes: scheduleData.notes || "",
            reviewNotes: scheduleData.reviewNotes || "",
          };

          // Save the schedule
          const saveResponse = await updateCASchedule(updatedSchedule);
          if (saveResponse) {
            processedSchedules.push(caKey);
          } else {
            skippedKeys.push(caKey);
          }
        } catch (error) {
          console.error(`Error processing ${caKey}:`, error);
          skippedKeys.push(caKey);
        }
      }

      handleCloseExcelUploadDialog();

      if (processedSchedules.length >= 0) {
        handleSuccess(
          `Successfully processed ${
            processedSchedules.length
          } CA schedule(s): ${processedSchedules.join(", ")}.\n${
            skippedKeys.length > 0
              ? `\nThe following CA schedules were skipped: ${skippedKeys.join(
                  ", "
                )}.\nThe uploaded data for these CA schedules may be empty or you have not created the CA schedules yet.\n`
              : ""
          }${
            caNameErrors.length > 0
              ? `\nThe following CA names for the uploaded data were not found: ${caNameErrors.join(
                  "\n"
                )}`
              : ""
          }`,
          true
        );
      } else {
        handleError(
          `No CA schedules were processed. ${
            skippedKeys.length > 0
              ? `Skipped: ${skippedKeys.join(
                  ", "
                )}. Please check the skipped CA schedules and ensure you have created them.`
              : ""
          }`
        );
      }
    } catch (error) {
      console.error("Error processing excel data:", error);
      handleError("Error processing excel data. Please try again.");
      setProcessing(false);
    }
  };

  // Column info dialog state
  const [infoOpen, setInfoOpen] = useState(false);

  const supportsHirePurchase = schedule.caType !== "S19D";
  const { selectedTabIndex: savedTab, setSelectedTabIndex: setActiveTab } =
    useTabPersistence(
      `${schedule.caType}-${schedule?.id || "default"}-selected-tab`,
      0
    );
  const activeTab = supportsHirePurchase ? savedTab : 0;
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const generateHirePurchaseDetails = (
    hpAdditions,
    hpDisposals,
    data,
    caType,
    caSettings
  ) => {
    const { additions, disposals } = generateHirePurchaseRows(
      hpAdditions,
      hpDisposals,
      data,
      caSettings,
      caType,
      yearOfAssessment
    );

    setHirePurchaseAdditions(additions);
    let newAdditionsTotal = calculateTotal(additions, true);
    setTotalHpAdditionsDetails(newAdditionsTotal);

    let newDisposalTotal = calculateTotal(disposals, true);
    setHirePurchaseDisposal(disposals);
    setTotalHpDisposalDetails(newDisposalTotal);

    return { additions, disposals, newAdditionsTotal, newDisposalTotal };
  };

  const generateCaSummary = (
    additions,
    disposals,
    hpAdditions,
    hpDisposals
  ) => {
    if (!hpAdditions) hpAdditions = hirePurchaseAdditions;
    if (!hpDisposals) hpDisposals = hirePurchaseDisposal;
    let groupedAA = {};
    let groupedIA = {};

    const processRows = (rows) => {
      if (!rows) return;
      rows.forEach((row) => {
        let caKey = `${row.caSummary}`;

        if (["S16 - LIA,S16 - IBA"].includes(caKey)) {
          if (row.s16Type) caKey = `${row.s16Type}`;
          else caKey = `${row.caName}`;
        }
        if (caKey === "S16" && row.isHirePurchase) {
          caKey = `${row.caName}`;
        }
        if (caKey && caKey !== "") {
          // Initialize both AA and IA
          if (!groupedAA[caKey]) groupedAA[caKey] = 0;
          if (!groupedIA[caKey]) groupedIA[caKey] = 0;

          // Sum up the values
          groupedAA[caKey] += Math.round(row.annualAllowance) || 0;
          groupedIA[caKey] += Math.round(row.initialAllowance) || 0;

          if (
            row.allowanceType === "Enhanced" &&
            Object.keys(row.enhanced).length > 0
          ) {
            groupedAA[caKey] += Math.round(row.enhanced.annualAllowance) || 0;
            groupedIA[caKey] += Math.round(row.enhanced.initialAllowance) || 0;
          }

          if (row.isDisregard && Object.keys(row.disregard).length > 0) {
            groupedAA[caKey] -= Math.round(row.disregard.annualAllowance) || 0;
            groupedIA[caKey] -= Math.round(row.disregard.initialAllowance) || 0;
          }
        }
      });
    };

    processRows(additions);
    processRows(disposals);
    processRows(hpAdditions);
    processRows(hpDisposals);

    setCaSummaryDetails(
      Object.keys(groupedAA).map((caSummary) => ({
        caSummary,
        totalAA: groupedAA[caSummary],
        totalIA: groupedIA[caSummary] || 0,
      }))
    );
  };

  const calculateTotalNetAA = (
    totalAddition,
    totalDisposal,
    totalHpAddition,
    totalHpDisposal
  ) => {
    if (!totalHpAddition) totalHpAddition = totalHpAdditionsDetails;
    if (!totalHpDisposal) totalHpDisposal = totalHpDisposalDetails;

    setTotalNetAA(
      (Math.round(totalAddition.initialAllowance) || 0) +
        Math.round(totalAddition.annualAllowance) +
        (Math.round(totalDisposal.initialAllowance) || 0) +
        Math.round(totalDisposal.annualAllowance) +
        (Math.round(totalHpAddition.initialAllowance) || 0) +
        Math.round(totalHpAddition.annualAllowance) +
        (Math.round(totalHpDisposal.initialAllowance) || 0) +
        Math.round(totalHpDisposal.annualAllowance)
    );
  };

  const handleAdditionsInputChange = (
    value,
    { index, field, allowanceType }
  ) => {
    let updatedRows = [...additionsDetails];

    if (
      ["currentYearDisposal", "currentAmount", "noOfYearClaims"].includes(field)
    ) {
      value = Number(getCommaOut(value));
    }

    inputHandlerSwitch(
      field,
      value,
      updatedRows[index],
      null,
      caSettings,
      yearOfAssessment,
      allowanceType
    );

    updatedRows[index] = updateRowValues(
      updatedRows[index],
      caSettings,
      yearOfAssessment,
      true
    );

    setAdditionDetails(updatedRows);

    let newAdditionTotal = calculateTotal(updatedRows);
    setTotalAdditionsDetails(newAdditionTotal);

    calculateTotalNetAA(newAdditionTotal, totalDisposalDetails);

    generateCaSummary(
      updatedRows,
      disposalDetails,
      hirePurchaseAdditions,
      hirePurchaseDisposal
    );
  };

  const handleHirePurchaseAdditionsInputChange = (
    value,
    { index, field, allowanceType }
  ) => {
    let updatedRows = [...hirePurchaseAdditions];

    inputHandlerSwitch(
      field,
      value,
      updatedRows[index],
      null,
      caSettings,
      yearOfAssessment,
      allowanceType,
      true
    );

    updatedRows[index] = updateRowValues(
      updatedRows[index],
      caSettings,
      yearOfAssessment,
      true
    );

    setHirePurchaseAdditions(updatedRows);

    let newAdditionTotal = calculateTotal(updatedRows, true);
    setTotalHpAdditionsDetails(newAdditionTotal);

    calculateTotalNetAA(
      totalAdditionsDetails,
      totalDisposalDetails,
      newAdditionTotal,
      totalHpDisposalDetails
    );

    generateCaSummary(
      additionsDetails,
      disposalDetails,
      updatedRows,
      hirePurchaseDisposal
    );
  };

  const handleDisposalInputChange = (
    value,
    { index, field, allowanceType }
  ) => {
    let updatedRows = [...disposalDetails];
    let row = { ...updatedRows[index] };

    inputHandlerSwitch(
      field,
      value,
      row,
      twdvDetails,
      caSettings,
      yearOfAssessment,
      allowanceType
    );

    updatedRows[index] = updateRowValues(row, caSettings, yearOfAssessment);

    const scheduleDetails = getCaSettingByKey(
      caSettings,
      updatedRows[index].caName
    );
    updatedRows[index] = calculateEnhancedSet(
      updatedRows[index],
      scheduleDetails
    );

    let newDisposalTotal = calculateTotal(updatedRows);
    setTotalDisposalDetails(newDisposalTotal);
    setDisposalDetails(
      updatedRows.sort((a, b) => a.yaOfPurchase - b.yaOfPurchase)
    );

    calculateTotalNetAA(totalAdditionsDetails, newDisposalTotal);

    generateCaSummary(
      additionsDetails,
      updatedRows,
      hirePurchaseAdditions,
      hirePurchaseDisposal
    );
  };

  const handleHirePurchaseDisposalInputChange = (
    value,
    { index, field, allowanceType }
  ) => {
    let updatedRows = [...hirePurchaseDisposal];
    let row = { ...updatedRows[index] };

    inputHandlerSwitch(
      field,
      value,
      row,
      twdvDetails,
      caSettings,
      yearOfAssessment,
      allowanceType,
      true
    );

    updatedRows[index] = updateRowValues(row, caSettings, yearOfAssessment);

    const scheduleDetails = getCaSettingByKey(
      caSettings,
      updatedRows[index].caName
    );
    updatedRows[index] = calculateEnhancedSet(
      updatedRows[index],
      scheduleDetails
    );

    let newDisposalTotal = calculateTotal(updatedRows, true);
    setTotalHpDisposalDetails(newDisposalTotal);
    setHirePurchaseDisposal(updatedRows);

    calculateTotalNetAA(
      totalAdditionsDetails,
      totalDisposalDetails,
      totalHpAdditionsDetails,
      newDisposalTotal
    );

    generateCaSummary(
      additionsDetails,
      disposalDetails,
      hirePurchaseAdditions,
      updatedRows
    );
  };

  const handleSave = () => {
    if (!canEditSchedule) return;
    if (caType === "S19D") {
      const invalidRow = [...disposalDetails, ...hirePurchaseDisposal].find(
        (row) => getNetDisposalError(row)
      );
      if (invalidRow) {
        handleError(`YA ${invalidRow.ya}: ${getNetDisposalError(invalidRow)}`);
        return;
      }
    }
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;

    saveSchedule(schedule, {
      additionsDetails,
      disposalDetails,
      hirePurchaseAdditions,
      hirePurchaseDisposal,
      totalNetAA,
      doc: JSON.stringify({
        caType,
        refSources,
        additionsDetails,
        disposalDetails,
        hirePurchaseAdditions,
        hirePurchaseDisposal,
        totalAdditionsDetails,
        totalDisposalDetails,
        totalHpAdditionsDetails,
        totalHpDisposalDetails,
        totalNetAA,
        caSummaryDetails,
        notes,
        reviewNotes,
      }),
    });
  };

  useEffect(() => {
    //init
    setLoading(true);

    if (schedule) {
      setScheduleNo(schedule.scheduleNo);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);

      setCaType(schedule.caType);
      setCaSettings(schedule.caSettings);
      setRefSources(schedule.refSources);
    }

    if (scheduleDetails) {
      let newAdditions = generateAdditions(
        scheduleDetails.dataRows?.additions,
        scheduleDetails.faAdditionsSummary,
        scheduleDetails.twdvSummary,
        schedule.caSettings,
        schedule.caType,
        yearOfAssessment,
        scheduleDetails.faRegisterRows || []
      );

      let newDisposal = generateDisposal(
        scheduleDetails.dataRows.disposal,
        scheduleDetails.twdvSummary,
        schedule.caSettings,
        schedule.caType,
        yearOfAssessment
      );
      setAdditionDetails(newAdditions);
      setTwdvDetails(scheduleDetails.twdvSummary);
      setDisposalDetails(newDisposal.sort((a, b) => a.ya - b.ya));

      let newAdditionsTotal = calculateTotal(newAdditions);
      let newDisposalTotal = calculateTotal(newDisposal);
      setTotalAdditionsDetails(newAdditionsTotal);
      setTotalDisposalDetails(newDisposalTotal);

      const {
        additions: hpAdditions,
        disposals: hpDisposal,
        newAdditionsTotal: totalHpAddition,
        newDisposalTotal: totalHpDisposal,
      } = generateHirePurchaseDetails(
        scheduleDetails.dataRows.hpAdditions,
        scheduleDetails.dataRows.hpDisposal,
        scheduleDetails.hirePurchaseSummary,
        schedule.caType,
        schedule.caSettings
      ) || {};

      calculateTotalNetAA(
        newAdditionsTotal,
        newDisposalTotal,
        totalHpAddition,
        totalHpDisposal
      );

      generateCaSummary(newAdditions, newDisposal, hpAdditions, hpDisposal);
    }

    setLoading(false);
  }, []);

  const createNewRow = () => ({
    ya: "",
    caName: "",
    caSummary: "",
    allowanceType: "Base",
    initialAllowanceRate: 0,
    s16Type: "",
    noOfYearClaims: 0,
    noOfYearClaimsBf: 0,
    cost: 0,
    twdvBf: 0,
    assetInUseAtFYE: caType === "S19D",
    currentAmount: 0,
    currentYearDisposal: 0,
    totalAmount: 0,
    isDeferred: false,
    isDisregard: false,
    disregardRate: 0,
    annualAllowance: 0,
    initialAllowance: 0,
    twdvCf: 0,
    noOfYearClaimsCf: 0,
    enhanced: {},
    disregard: {},
  });

  const addNewRow = (isHirePurchase) => {
    const newRow = createNewRow();

    if (!isHirePurchase) {
      const updatedData = [...disposalDetails];
      updatedData.push(newRow);
      setDisposalDetails(updatedData);
    } else {
      const hpRow = {
        ...newRow,
        principalPaid: 0,
        manualData: true,
      };
      const updatedData = [...hirePurchaseDisposal, hpRow];
      setHirePurchaseDisposal(updatedData);
    }
  };

  const handlePopulatePriorYa = (key, noOfYearClaims) => {
    if (!canEditSchedule) return;
    const option = priorYaDisposalOptions.find((entry) => entry.key === key);
    if (
      !option ||
      option.alreadyAdded ||
      option.disabledReason ||
      !Number.isInteger(noOfYearClaims) ||
      noOfYearClaims <= 0 ||
      noOfYearClaims < Number(option.noOfYearsBf)
    )
      return;

    const setting = getCaSettingByKey(caSettings, option.caName);
    const isS16 = caType === "S16";
    const row = {
      ...createNewRow(),
      ya: option.yaOfPurchase,
      caName: isS16 ? "S16 - (LIA / IBA)" : option.caName,
      caSummary: isS16 ? option.caName : setting.caSummary,
      s16Type: isS16 ? option.caName : "",
      noOfYearClaims,
      noOfYearClaimsBf: option.noOfYearsBf,
      noOfYearClaimsCf: Math.max(0, Number(option.noOfYearsBf) - 1),
      ...(caType === "S19D" ? { cost: Number(option.cost) || 0 } : {}),
    };
    const populatedRow = populateTwdvToDisposal(
      row,
      twdvDetails,
      caSettings,
      yearOfAssessment
    );
    const updatedRows = [
      ...disposalDetails,
      updateRowValues(populatedRow, caSettings, yearOfAssessment),
    ].sort((a, b) => a.ya - b.ya);
    const totals = calculateTotal(updatedRows);
    setDisposalDetails(updatedRows);
    setTotalDisposalDetails(totals);
    calculateTotalNetAA(totalAdditionsDetails, totals);
    generateCaSummary(
      additionsDetails,
      updatedRows,
      hirePurchaseAdditions,
      hirePurchaseDisposal
    );
    setPopulatePriorYaOpen(false);
  };

  const removeRow = (rowIndex, isHirePurchase = false) => {
    if (!isHirePurchase) {
      let updatedRows = disposalDetails.filter((_, i) => i !== rowIndex);
      setDisposalDetails(updatedRows);
      let newDisposalTotal = calculateTotal(updatedRows);
      setTotalDisposalDetails(newDisposalTotal);

      calculateTotalNetAA(
        totalAdditionsDetails,
        newDisposalTotal,
        totalHpAdditionsDetails,
        totalHpDisposalDetails
      );

      generateCaSummary(
        additionsDetails,
        updatedRows,
        hirePurchaseAdditions,
        hirePurchaseDisposal
      );
    } else {
      let updatedRows = hirePurchaseDisposal.filter((_, i) => i !== rowIndex);
      setHirePurchaseDisposal(updatedRows);
      let newHpDisposalTotal = calculateTotal(updatedRows);
      setTotalHpDisposalDetails(newHpDisposalTotal);

      calculateTotalNetAA(
        totalAdditionsDetails,
        totalDisposalDetails,
        totalHpAdditionsDetails,
        newHpDisposalTotal
      );

      generateCaSummary(
        additionsDetails,
        disposalDetails,
        hirePurchaseAdditions,
        updatedRows
      );
    }
  };

  return (
    <Box sx={{ padding: 4 }}>
      {loading ? (
        <ComponentLoading />
      ) : (
        <>
          <ScheduleHeader
            {...{
              title: schedule.type,
              companyName: company.name,
              uen: company.uen,
              yearOfAssessment: company.yearOfAssessment,
              financialYearStart: company.financialYearStart,
              financialYearEnd: company.financialYearEnd,
              deleteSchedule,
              handleSave,
              canEditSchedule,
              handleExcel: handleOpenExcelUploadDialog,
              onHelpClick: () => setInfoOpen(true),
            }}
          >
            <PdfGenerator
              schedule={schedule}
              company={company}
              caType={caType}
              refSources={refSources}
              additionsDetails={additionsDetails}
              disposalDetails={disposalDetails}
              hirePurchaseAdditions={hirePurchaseAdditions}
              hirePurchaseDisposal={hirePurchaseDisposal}
              totalAdditionsDetails={totalAdditionsDetails}
              totalDisposalDetails={totalDisposalDetails}
              totalHpAdditionsDetails={totalHpAdditionsDetails}
              totalHpDisposalDetails={totalHpDisposalDetails}
              totalNetAA={totalNetAA}
              caSummaryDetails={caSummaryDetails}
              notes={notes}
              reviewNotes={reviewNotes}
            />
          </ScheduleHeader>

          <br />

          {supportsHirePurchase && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                background: (theme) =>
                  `linear-gradient(145deg, ${
                    theme.palette.background.paper
                  }, ${alpha(theme.palette.primary.main, 0.01)})`,
                "&:hover": {
                  borderColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.3),
                  boxShadow: (theme) =>
                    `0px 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                  transform: "translateY(-2px)",
                },
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label='CA Claims tabs'
                variant='fullWidth'
                sx={{
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  background: (theme) => theme.palette.background.paper,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 64,
                    fontSize: "1rem",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.08),
                    },
                  },
                  "& .Mui-selected": (theme) => ({
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  }),
                }}
              >
                <Tab
                  label='YA CA Claims'
                  icon={<BalanceIcon />}
                  iconPosition='start'
                />
                <Tab
                  label='Hire Purchase'
                  icon={<ReceiptIcon />}
                  iconPosition='start'
                />
              </Tabs>
            </Paper>
          )}

          {/* YA CA Claims Tab Content */}
          {activeTab === 0 && (
            <>
              <SectionBox
                title='Prior YA CA claims'
                caType={caType}
                scheduleNo={scheduleNo}
                showAddButton={!!addNewRow}
                onAdd={() => addNewRow(false)}
                contentSx={{ p: 0 }}
                addButtonInHeader={true}
                id='disposal-sectionbox'
                isFullscreen={fullscreenSection === "disposal"}
                onFullscreenChange={(isFs) =>
                  setFullscreenSection(isFs ? "disposal" : null)
                }
                icon={<CardMembershipIcon color='primary' />}
                canEdit={canEditSchedule}
                headerActions={
                  canEditSchedule && (
                    <Button
                      size='small'
                      startIcon={<PlaylistAddIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPopulatePriorYaOpen(true);
                      }}
                    >
                      Add from FA Disposal
                    </Button>
                  )
                }
              >
                <CaTable
                  yearOfAssessment={yearOfAssessment}
                  caType={caType}
                  caSettings={caSettings}
                  dataType='Disposal'
                  dataRows={disposalDetails}
                  setDataRows={setDisposalDetails}
                  dataTotalRows={totalDisposalDetails}
                  removeRow={removeRow}
                  onChange={handleDisposalInputChange}
                  fullscreen={fullscreenSection === "disposal"}
                  canEdit={canEditSchedule}
                />
              </SectionBox>

              <Box sx={{ mb: 3 }} />

              <SectionBox
                title='Current YA CA claims'
                caType={caType}
                scheduleNo={scheduleNo}
                contentSx={{ p: 0 }}
                addButtonInHeader={false}
                id='additions-sectionbox'
                isFullscreen={fullscreenSection === "additions"}
                onFullscreenChange={(isFs) =>
                  setFullscreenSection(isFs ? "additions" : null)
                }
                icon={<BalanceIcon color='primary' />}
              >
                <CaTable
                  yearOfAssessment={yearOfAssessment}
                  caType={caType}
                  caSettings={caSettings}
                  dataType='Additions'
                  dataRows={additionsDetails}
                  setDataRows={setAdditionDetails}
                  dataTotalRows={totalAdditionsDetails}
                  onChange={handleAdditionsInputChange}
                  fullscreen={fullscreenSection === "additions"}
                  canEdit={canEditSchedule}
                />
              </SectionBox>
            </>
          )}

          {/* Hire Purchase Tab Content */}
          {supportsHirePurchase && activeTab === 1 && (
            <>
              <SectionBox
                title='Prior YA Hire Purchase'
                caType={caType}
                scheduleNo={scheduleNo}
                contentSx={{ p: 0 }}
                id='hire_purchase_disposal-sectionbox'
                addButtonInHeader={true}
                showAddButton={!!addNewRow}
                onAdd={() => addNewRow(true)}
                isFullscreen={fullscreenSection === "hire_purchase"}
                onFullscreenChange={(isFs) =>
                  setFullscreenSection(isFs ? "hire_purchase" : null)
                }
                icon={<CreditScoreIcon color='primary' />}
              >
                <CaTable
                  yearOfAssessment={yearOfAssessment}
                  caType={caType}
                  caSettings={caSettings}
                  dataType='Disposal'
                  dataRows={hirePurchaseDisposal}
                  setDataRows={setHirePurchaseDisposal}
                  dataTotalRows={totalHpDisposalDetails}
                  onChange={handleHirePurchaseDisposalInputChange}
                  fullscreen={fullscreenSection === "hire_purchase"}
                  isHirePurchase
                  canEdit={canEditSchedule}
                  removeRow={removeRow}
                />
              </SectionBox>
              <SectionBox
                title='Current YA Hire Purchase'
                caType={caType}
                scheduleNo={scheduleNo}
                contentSx={{ p: 0 }}
                addButtonInHeader={false}
                id='hire_purchase_addition-sectionbox'
                isFullscreen={fullscreenSection === "hire_purchase"}
                onFullscreenChange={(isFs) =>
                  setFullscreenSection(isFs ? "hire_purchase" : null)
                }
                icon={<PaymentsIcon color='primary' />}
              >
                <CaTable
                  yearOfAssessment={yearOfAssessment}
                  caType={caType}
                  caSettings={caSettings}
                  dataType='Additions'
                  dataRows={hirePurchaseAdditions}
                  setDataRows={setHirePurchaseAdditions}
                  dataTotalRows={totalHpAdditionsDetails}
                  onChange={handleHirePurchaseAdditionsInputChange}
                  fullscreen={fullscreenSection === "hire_purchase"}
                  isHirePurchase
                  canEdit={canEditSchedule}
                />
              </SectionBox>
            </>
          )}

          <Box sx={{ mb: 4 }} />

          <CaSummary caType={caType} dataRows={caSummaryDetails} />

          <Divider sx={{ marginY: 4 }} />
          <ScheduleNotes
            notes={notes}
            setNotes={setNotes}
            reviewNotes={reviewNotes}
            setReviewNotes={setReviewNotes}
            enableEdit={canEditSchedule}
          />

          <CaExcelUpload
            open={excelDialogOpen}
            handleCloseDialog={handleCloseExcelUploadDialog}
            handleExcelUpload={handleExcelUpload}
            processing={processing}
          />

          <ColumnInfo
            infoOpen={infoOpen}
            setInfoOpen={setInfoOpen}
            caType={caType}
            caSettings={caSettings}
            isHirePurchase={activeTab === 1}
          />
          {populatePriorYaOpen && (
            <PopulatePriorYaDialog
              options={priorYaDisposalOptions}
              caType={caType}
              onClose={() => setPopulatePriorYaOpen(false)}
              onPopulate={handlePopulatePriorYa}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default CaSchedule;
